import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trophy, Lock, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const token = searchParams.get("token");

  const handleReset = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La password deve avere almeno 6 caratteri");
      return;
    }
    if (password !== confirmPwd) {
      toast.error("Le password non corrispondono");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reimpostata con successo!");
      setDone(true);
    } catch (err) {
      toast.error(err.message || "Errore nel reset della password");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
        <Card className="w-full max-w-md bg-white/[0.07] backdrop-blur-xl border border-white/10">
          <CardContent className="p-8 text-center">
            <p className="text-white">Token mancante. Richiedi un nuovo link di reset dalla pagina di login.</p>
            <Button onClick={() => navigate("/")} className="mt-4 bg-[#c8e414] text-slate-900 font-bold rounded-sm">Vai al Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-white/[0.07] backdrop-blur-xl border border-white/10 shadow-2xl">
        <CardContent className="p-8 space-y-6">
          <div className="flex justify-center">
            <div className="h-14 w-14 bg-[#c8e414] rounded-full flex items-center justify-center shadow-lg">
              <Trophy className="h-7 w-7 text-slate-900" strokeWidth={2.5} />
            </div>
          </div>
          <h1 className="font-heading text-2xl font-bold text-white uppercase text-center">Nuova Password</h1>

          {done ? (
            <div className="text-center space-y-4 py-4">
              <CheckCircle className="h-12 w-12 text-green-400 mx-auto" />
              <p className="text-white" data-testid="reset-success-msg">Password reimpostata con successo!</p>
              <Button onClick={() => navigate("/")} className="bg-[#c8e414] hover:bg-[#b5d010] text-slate-900 font-bold uppercase rounded-sm" data-testid="reset-go-login">
                Vai al Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleReset} className="space-y-3" data-testid="reset-form">
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-white/40" />
                <Input data-testid="reset-password-input" type="password" placeholder="Nuova password (min 6 caratteri)" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 rounded-sm h-10 focus:border-[#c8e414]" />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-white/40" />
                <Input data-testid="reset-confirm-input" type="password" placeholder="Conferma password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required className="pl-10 bg-white/10 border-white/10 text-white placeholder:text-white/40 rounded-sm h-10 focus:border-[#c8e414]" />
              </div>
              <Button data-testid="reset-submit-btn" type="submit" disabled={submitting} className="w-full bg-[#c8e414] hover:bg-[#b5d010] text-slate-900 font-bold uppercase tracking-wide rounded-sm py-5 gap-2">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                Reimposta Password
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
